import { DateRange } from '@app/shared/models/date-range';
import { DateUtils } from '@app/utils/date';

/**
 * Calculates a date range based on the month of the given reference date
 * @param referenceDate Date for which the date range is calculated
 */
export function currentMonth(referenceDate: Date): DateRange {
  return dateRange(DateUtils.firstDayOfMonth(referenceDate), DateUtils.lastDayOfMonth(referenceDate));
}

/**
 * Calculates a date range based on the quarter of the given reference date
 * @param referenceDate Date for which the date range is calculated
 */
export function currentQuarter(referenceDate: Date): DateRange {
  return dateRange(DateUtils.firstDayOfQuarter(referenceDate), DateUtils.lastDayOfQuarter(referenceDate));
}

/**
 * Calculates a date range based on the week of the given reference date
 * @param referenceDate Date for which the date range is calculated
 */
export function currentWeek(referenceDate: Date): DateRange {
  return dateRange(DateUtils.firstDayOfWeek(referenceDate), DateUtils.lastDayOfWeek(referenceDate));
}

/**
 * Calculates a date range based on the month before the given reference date
 * @param referenceDate Date for which the date range is calculated
 */
export function lastMonth(referenceDate: Date): DateRange {
  const lastMonth = DateUtils.subMonths(referenceDate, 1);

  return dateRange(DateUtils.firstDayOfMonth(lastMonth), DateUtils.lastDayOfMonth(lastMonth));
}

/**
 * Calculates a date range based on the quarter before the given reference date
 * @param referenceDate Date for which the date range is calculated
 */
export function lastQuarter(referenceDate: Date): DateRange {
  const lastQuarter = DateUtils.subQuarters(referenceDate, 1);

  return dateRange(DateUtils.firstDayOfQuarter(lastQuarter), DateUtils.lastDayOfQuarter(lastQuarter));
}

/**
 * Calculates a date range based on the week before the given reference date
 * @param referenceDate Date for which the date range is calculated
 */
export function lastWeek(referenceDate: Date): DateRange {
  const todayLastWeek = DateUtils.subWeeks(referenceDate, 1);

  return currentWeek(todayLastWeek);
}

/**
 * Returns the given reference date as a date range (from = referenceDate, end = referenceDate)
 * @param referenceDate Date for which the date range is calculated
 */
export function todayAsRange(referenceDate: Date): DateRange {
  return dateRange(DateUtils.startOfDay(referenceDate), referenceDate);
}

/**
 * Returns the day before the given reference date as a date range (from = referenceDate - 1, end = referenceDate - 1)
 * @param referenceDate Date for which the date range is calculated
 */
export function yesterdayAsRange(referenceDate: Date): DateRange {
  const yesterday = DateUtils.subDays(referenceDate, 1);

  return dateRange(yesterday, yesterday);
}

function dateRange(from: Date, to: Date): DateRange {
  return { from, to };
}
